import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    adminGetAgents,
    AdminGetAgentsData,
    getAvalibleAgentsInChildOrganisation,
    GetOrganisationData,
    updateOrganisation,
    UpdateOrganisationData,
} from "@/api";
import { appState } from "@/app/appState";
import { agent_list_item } from "@/app/typing/agents";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { ActionHandler, DefEdit } from "@/components/UI/tools";
import { Add_new, Edit, Save } from "@/components/UI/svgs";
import { canWrite } from "./tree";
import { EmptyRow, Pill, Row, Section, SelectField, TextField } from "./ui";

export const nonePromptSet = () => ({ id: undefined, name: appState.t("settings.org.noDefaultPromptSet") });

export const usePromptSets = (orgId: number) => {
    const [owned, setOwned] = useState<agent_list_item[]>([]);
    const [assignable, setAssignable] = useState<agent_list_item[]>([]);
    const [loading, setLoading] = useState(false);

    const loadOwned = Request<AdminGetAgentsData, any>(
        adminGetAgents,
        () => ({ body: { page_size: 999999, page_number: 0, search: "", organisation_id: orgId } }),
        setLoading,
        (data: any) => setOwned(data.agents ?? []),
        () => setOwned([]),
        () => orgId > 0,
    );

    const loadAssignable = Request<GetOrganisationData, any>(
        getAvalibleAgentsInChildOrganisation,
        () => ({ body: { id: orgId } }),
        () => { },
        (data: any) => setAssignable(data.agents ?? []),
        () => setAssignable([]),
        () => orgId > 0,
    );

    const reload = () => {
        loadOwned();
        loadAssignable();
    };

    useEffect(() => {
        setOwned([]);
        setAssignable([]);
        reload();
    }, [orgId]);

    return { owned, assignable, loading, reload };
};

/** The organisation's name, its prompt sets and which one it runs on. */
export const PromptsSection = observer(({
    node,
    defaultUsage,
    onOpenPromptSet,
    onNewPromptSet,
    onRefresh,
}: {
    node: organisation_tree_node;
    // How many reachable organisations use each prompt set as their default.
    defaultUsage: Map<number, number>;
    onOpenPromptSet: (setId: number) => void;
    onNewPromptSet: () => void;
    onRefresh: () => void;
}) => {
    const writable = canWrite(node);
    const isActive = node.id === appState.user?.active_organisation?.id;

    const [name, setName] = useState(node.name);
    const [editingName, setEditingName] = useState(false);
    const [loading, setLoading] = useState(false);
    const { owned, assignable, reload } = usePromptSets(node.id);

    useEffect(() => {
        setName(node.name);
        setEditingName(false);
    }, [node.id, node.name]);

    const save = Request<UpdateOrganisationData, any, { name: string; defaultSetId?: number }>(
        updateOrganisation,
        (props) => ({
            body: {
                id: node.id,
                name: props.name,
                default_prompt_set_id: props.defaultSetId ?? null,
            },
        }),
        setLoading,
        () => {
            setEditingName(false);
            onRefresh();
            reload();
            if (isActive) appState.getOrgs();
        },
        (error) => {
            setName(node.name);
            setEditingName(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.org.errorSave"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        (props) => props.name.trim().length > 0,
    );

    const cancelRename = () => {
        setName(node.name);
        setEditingName(false);
    };

    return (
        <>
            <Section title={appState.t("settings.org.organisation")}>
                {editingName ? (
                    <div className="row-form row-form-active">
                        <div className="flex flex-row items-center gap-[.25rem]">
                            <TextField value={name} onChange={setName} autoFocus />
                            <ActionHandler
                                title={appState.t("settings.org.saveName")}
                                funq={() => save({ name, defaultSetId: node.default_prompt_set_id ?? undefined })}
                                disabled={loading || name.trim().length === 0}
                            >
                                <Save size={16} />
                            </ActionHandler>
                            <ActionHandler title={appState.t("common.cancel")} funq={cancelRename} disabled={loading}>
                                {appState.t("common.cancel")}
                            </ActionHandler>
                        </div>
                    </div>
                ) : (
                    <Row
                        trailing={
                            writable && (
                                <ActionHandler
                                    title={appState.t("settings.org.rename")}
                                    funq={() => DefEdit(true, node.id, setEditingName, "organisation")}
                                    disabled={loading}
                                >
                                    <Edit size={16} />
                                </ActionHandler>
                            )
                        }
                    >
                        <p className="l truncate">{node.name}</p>
                    </Row>
                )}
            </Section>

            <Section
                title={appState.t("settings.org.promptSets")}
                action={
                    writable ? (
                        <ActionHandler title={appState.t("settings.org.newPromptSetTitle")} funq={onNewPromptSet}>
                            <Add_new size={16} />
                        </ActionHandler>
                    ) : undefined
                }
            >
                {owned.length === 0 && <EmptyRow>{appState.t("settings.org.noPromptSets")}</EmptyRow>}
                {owned.map((set) => {
                    const usage = defaultUsage.get(set.id) ?? 0;
                    return (
                        <Row
                            key={set.id}
                            onClick={() => onOpenPromptSet(set.id)}
                            chevron
                            trailing={
                                <div className="flex flex-row items-center gap-[.25rem]">
                                    {set.owned === false && <Pill title={appState.t("settings.org.inheritedTitle")}>{appState.t("common.inherited")}</Pill>}
                                    <Pill tone={usage > 0 ? "accent" : "neutral"}>
                                        {usage > 0
                                            ? appState.t("settings.org.defaultFor", { n: usage })
                                            : appState.t("settings.org.unused")}
                                    </Pill>
                                </div>
                            }
                        >
                            <p className="l truncate">{set.name}</p>
                        </Row>
                    );
                })}
            </Section>

            <Section title={appState.t("settings.org.defaultPromptSet")} hint={appState.t("settings.org.defaultHint")}>
                <div className="row-form">
                    <SelectField
                        disabled={!writable || loading}
                        value={node.default_prompt_set_id ?? undefined}
                        onChange={(value) => save({ name: node.name, defaultSetId: value })}
                        options={[
                            nonePromptSet(),
                            ...assignable.map((set) => ({ id: set.id, name: set.name })),
                        ]}
                    />
                </div>
            </Section>
        </>
    );
});
